/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import _ from 'lodash';
import clsx from 'clsx';

import { DateTimeFormatter, LocalTime } from '@js-joda/core';
import { DisplayDate } from '@tock/time';
import { ExperienceOption } from 'components/business/waitlist-dialog/WaitlistDialog';
import { FormBlock, Typography } from 'omakase';
import { Grid } from 'ui';
import { IconChevronSmallDown, IconChevronSmallUp } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { consumer, safeToNumber } from '@tock/coretypes';
import { dateFieldContainer } from 'components/business/waitlist-dialog/waitlistDialogCssDefs';
import { getOpenDates } from 'utils/offerings';
import { getTimeFormatter } from '@tock/i18n';
import { pluralize } from '@tock/shared/utils/pluralize';
import { ticketTypeOpenDates } from 'utils/calendar';
import BasicDropdown from '@tock/shared/components/inputs/basic-dropdown/BasicDropdown';
import ConsumerCalendarMultiDaySelect from 'components/calendar/ConsumerCalendarMultiDaySelect';
import ConsumerDropdownOption from 'components/business/ConsumerDropdownOption';
import Field from '@tock/shared/components/forms/Field';
import Fields from '@tock/shared/components/forms/Fields';
import Textarea from '@tock/shared/components/inputs/Textarea';

interface Props {
  business: Partial<consumer.IBusinessConfig>;
  desiredEndTime: ReduxFormField<string>;
  desiredStartTime: ReduxFormField<string>;
  /** Deprecated */
  displayedTimes: string[];
  experience: ReduxFormField<number>;
  experienceOptions: ExperienceOption[];
  guestNote: ReduxFormField<string>;
  offerings: consumer.ConsumerOfferings;
  partySize: ReduxFormField<number>;
  partySizeOptions: any[];
  selectedDates: ReduxFormField<string[]>;
  selectedExperience: ExperienceOption;
  selectedPartySize: SelectOption;
  selectedDesiredStartTime: string;
  selectedDesiredEndTime: string;
  showError(message: string): void;
  submitting: boolean;
  ticketTypeCalendarDetails: consumer.TicketTypeCalendarDetails[];
}

/**
 * Constrain time options to a min/max so that the start time cannot exceed
 * the end time and the end time cannot precede the start time
 *
 * @param minTime - minimum selectable time
 * @param maxTime - maximum selectable time
 * @param timeOptions - sorted array (ASC) of all possible time selections
 */
export const constrainTimeOptions = (minTime: string, maxTime: string, timeOptions: string[]) => {
  const minTimeIdx = timeOptions.indexOf(minTime);
  const maxTimeIdx = timeOptions.indexOf(maxTime);

  const startTimes = maxTimeIdx !== -1 ? timeOptions.slice(0, maxTimeIdx + 1) : [...timeOptions];
  const endTimes = minTimeIdx !== -1 ? timeOptions.slice(minTimeIdx) : [...timeOptions];

  return { startTimes, endTimes };
};

const WaitlistDialogPreferences: React.FC<Props> = (props) => {
  const [showExpandedCalendar, setShowExpandedCalendar] = useState(false);

  const {
    business,
    desiredEndTime,
    desiredStartTime,
    displayedTimes,
    experience,
    experienceOptions,
    guestNote,
    offerings,
    partySize,
    partySizeOptions,
    submitting,
    selectedDates,
    selectedExperience,
    selectedPartySize,
    selectedDesiredStartTime,
    selectedDesiredEndTime,
    ticketTypeCalendarDetails,
  } = props;
  const selectedDatesRef = useRef<HTMLButtonElement | null>(null);

  const handleExperienceChange = (newExperience: ExperienceOption) => {
    const { experience, experienceOptions, partySize } = props;
    const selectedExperience = _.find(
      experienceOptions,
      (entry) => entry.value === newExperience.value
    );

    if (!selectedExperience) {
      return;
    }

    experience.onChange(newExperience.value);
    if (!_.includes(selectedExperience.sizes, partySize.value)) {
      partySize.onChange(selectedExperience.sizes[0]);
    }
  };

  const handlePartySizeChange = (option: SelectOption) => {
    const { partySize } = props;

    if (typeof option.value === 'number') {
      partySize.onChange(option.value);
    }
  };

  const handleToggleCalendar = (e: React.MouseEvent) => {
    e.preventDefault();
    props.selectedDates.onBlur(); // mark touched so validation runs onChange
    selectedDatesRef.current?.focus();
    setShowExpandedCalendar(!showExpandedCalendar);
  };

  const handleClickDate = (clickedDate: string) => {
    const { selectedDates, showError } = props;
    let currentDates = _.clone(selectedDates.value);
    if (_.includes(currentDates, clickedDate)) {
      _.remove(currentDates, (date) => date === clickedDate);
    } else {
      if (selectedDates.value.length >= 10) {
        showError('You cannot select more than ten days.');
        return;
      }
      currentDates = currentDates.length > 0 ? _.concat(currentDates, clickedDate) : [clickedDate];
    }
    selectedDates.onChange(currentDates);
  };

  const handleDesiredStartTime = (clickedTime: string) => {
    desiredStartTime.onChange(clickedTime);
  };
  const handleDesiredEndTime = (clickedTime: string) => {
    desiredEndTime.onChange(clickedTime);
  };

  const handleResetCalendar = () => {
    props.selectedDates.onChange([]);
  };

  const iconClassName = clsx('WaitlistModal-dropdownSelectorIcon tock-icon icon_small-arrow-down', {
    'is-open': showExpandedCalendar,
  });
  const classNameCalendar = clsx('Select', 'Select--strip', 'Select--minimal', 'BasicDropdown', {
    'is-open': showExpandedCalendar,
  });

  const offeredDates = selectedExperience.value
    ? ticketTypeOpenDates(ticketTypeCalendarDetails, safeToNumber(selectedExperience.value))
    : getOpenDates(offerings);

  const timeOptions = constrainTimeOptions(
    selectedDesiredStartTime,
    selectedDesiredEndTime,
    displayedTimes
  );

  return (
    <>
      <FormBlock showDivider={false}>
        <Typography variant="body02">
          Tell us what you're interested in and we'll let you know if something opens up.
        </Typography>
        <Typography variant="heading04">{business.name}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Field label="Party size" labelId="partySize" field={partySize} disabled={submitting}>
              <BasicDropdown
                labelId="partySize"
                component={ConsumerDropdownOption}
                dropdownTestId="party-size-dropdown"
                onSelect={handlePartySizeChange}
                options={partySizeOptions}
                selectedOption={selectedPartySize}
              />
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Experience" labelId="experience" field={experience}>
              <BasicDropdown
                labelId="experience"
                component={ConsumerDropdownOption}
                dropdownTestId="experience-dropdown"
                onSelect={handleExperienceChange}
                options={experienceOptions}
                selectedOption={selectedExperience}
              />
            </Field>
          </Grid>
        </Grid>
        <Fields modifierClassName="Fields--above-m-landscape" key="fields-2">
          <Field label="Date">
            <button
              className={classNameCalendar}
              aria-expanded={showExpandedCalendar}
              title="Desired reservation date"
              onClick={handleToggleCalendar}
              aria-haspopup
              ref={selectedDatesRef}
            >
              <div className="WaitlistModal-dropdownSelectorContent Select-indicator">
                <Typography variant="body02" id="selectedDates">
                  {!_.isEmpty(selectedDates.value) ? (
                    selectedDates.value.length === 1 ? (
                      <DisplayDate dateValue={selectedDates.value[0]} displayMonthAs="short" />
                    ) : (
                      `${pluralize(selectedDates.value.length, 'day')} selected`
                    )
                  ) : (
                    'Select dates'
                  )}
                </Typography>
                <IconMigrationUtil
                  legacyIcon={<i className={iconClassName} />}
                  newIcon={showExpandedCalendar ? <IconChevronSmallUp /> : <IconChevronSmallDown />}
                />
              </div>
            </button>
          </Field>
        </Fields>
        <div>
          <p className="Label">Time</p>
          <div css={dateFieldContainer}>
            <Field field={desiredStartTime} disabled={submitting}>
              <BasicDropdown
                component={({ option }) => {
                  const formatted = LocalTime.parse(option, DateTimeFormatter.ofPattern('HH:mm'));
                  return (
                    <div className="ConsumerDropdownOption">
                      <Typography color="inherit" component="span" variant="body02">
                        {getTimeFormatter(business?.locale || 'en-us').format(formatted)}
                      </Typography>
                    </div>
                  );
                }}
                dropdownTestId="desired-start-time-dropdown"
                onSelect={handleDesiredStartTime}
                options={timeOptions.startTimes}
                selectedOption={selectedDesiredStartTime}
              />
            </Field>
            <p>to</p>
            <Field field={desiredEndTime} disabled={submitting}>
              <BasicDropdown
                component={({ option }) => {
                  const formatted = LocalTime.parse(option, DateTimeFormatter.ofPattern('HH:mm'));
                  return (
                    <div className="ConsumerDropdownOption">
                      <Typography color="inherit" component="span" variant="body02">
                        {getTimeFormatter(business?.locale || 'en-us').format(formatted)}
                      </Typography>
                    </div>
                  );
                }}
                dropdownTestId="desired-end-time-dropdown"
                onSelect={handleDesiredEndTime}
                options={timeOptions.endTimes}
                selectedOption={selectedDesiredEndTime}
              />
            </Field>
          </div>
        </div>

        <Field labelFor="guest-note" label="Notes" field={guestNote}>
          <Field field={guestNote}>
            <Textarea
              id="guest-note"
              disabled={submitting}
              field={guestNote}
              maxLength={255}
              placeholder="Please specify anything else we should know"
            />
          </Field>
        </Field>
      </FormBlock>
      {showExpandedCalendar && (
        <div className="Consumer-waitlistDropdown">
          <ConsumerCalendarMultiDaySelect
            availabilityOverride={true}
            onResetCalendar={handleResetCalendar}
            onToggleCalendar={handleToggleCalendar}
            lockedDates={{}}
            offeredDates={offeredDates}
            onSelect={handleClickDate}
            openDates={offeredDates}
            selectedDates={selectedDates.value}
            soldDates={{}}
            wideView={false}
          />
        </div>
      )}
    </>
  );
};

export default WaitlistDialogPreferences;
